import { Form, Formik } from "formik";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { DatePicker } from "../../components/DatePicker";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import Select, { Option } from "../../components/Select";
import { addChecksIndividualValidation } from "../../utils/validationSchema";
import { showError } from "../../utils/error-handling";
import { MIN_AGE_APPLICATION } from "../../utils/constants";
import { useCommonTitles } from "../../data/useCommonTitles";
import { useState } from "react";
import { NewChecksFrame } from "./NewChecksFrame";
import { Modal } from "../../components/Modal";
import { useCreateScreeningRecord } from "../../data/useCreateScreeningRecord";
import { useMembershipDivisionTeam } from "../../hooks/useMembershipDivisionTeam";

const rowProps: any = {
  flexWrap: "wrap",
  gap: "16",
  my: "4",
};

const inputProps = {
  minWidth: 200,
  flex: 1,
};

const genderOptions = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

const Header = () => {
  return (
    <>
      <H2>Add Individual</H2>
      <Paragraph>
        Please add the individual to perform the screening on.
      </Paragraph>
    </>
  );
};

export const AddCheckIndividual = () => {
  const [doubleClick, setDoubleClick] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkpointIds = location.state?.checkpointIds;

  const showAddressStep = location.state?.showAddressStep ?? true;

  const isPepAndSanctionsSelected = location.state?.isPepAndSanctionsSelected;

  const isAdverseMediaSelected = location.state?.isAdverseMediaSelected;

  // const showMonitoringStep = location.state?.showMonitoringStep ?? true;

  const [isNoDOBModalOpen, setIsNoDOBModalOpen] = useState(false);
  const [formData, setFormData] = useState(null);

  const { createScreeningRecord, isLoading } = useCreateScreeningRecord();

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();

  const { data } = useCommonTitles();

  const titleOptions = data?.data.map(({ name }) => ({
    label: name,
    value: name,
  })) as Option[];

  const handleCreateIndividual = (form: any) => {
    setFormData(form);
    if (form.dateOfBirth) {
      handleSubmit(form);
    } else {
      setIsNoDOBModalOpen(true);
    }
  };

  const handleNext = () => {
    handleSubmit(formData);
  };

  const handleSubmit = (form) => {
    setDoubleClick(true);

    const dob = form.dateOfBirth
      ? DateTime.fromJSDate(form.dateOfBirth as Date).toFormat("yyyy-MM-dd")
      : null;

    createScreeningRecord(
      {
        checkpointIds,
        subject: "individual",
        primaryDivision: form.division,
        primaryTeam: form.team,
        payload: {
          gender: form.gender,
          title: form?.title?.trim() || null,
          first_name: form.firstName,
          date_of_birth: dob,
          last_name: form.lastName,
          middle_name: form.middleName,
          email: form.email,
          external_reference: form.yourReference?.trim() || null,
          is_check_individual: true,
        },
      },
      {
        onSuccess: ({ mandateId, recordId }) => {
          const state = {
            checkpointIds: checkpointIds,
            showAddressStep: showAddressStep,
            // showMonitoringStep: showMonitoringStep,
            isPepAndSanctionsSelected: isPepAndSanctionsSelected,
            isAdverseMediaSelected: isAdverseMediaSelected,
          };

          if (showAddressStep) {
            navigate(`/screening/${mandateId}/address/${recordId}`, { state });
          }
          // else if ( !showAddressStep ) { //&& showMonitoringStep
          //   navigate(`/checks/${mandateId}/monitoring/${recordId}`, { state });
          // }
          else {
            navigate(`/screening/${mandateId}/run-screening/${recordId}`, {
              state,
            });
          }
        },
        onError: (err, stage) => {
          setDoubleClick(false);
          showError(
            err,
            stage === "subject"
              ? "A problem occurred while creating the individual."
              : "A problem occurred while creating the check."
          );
        },
      }
    );
  };

  return (
    <NewChecksFrame step="individuals" showAddressStep={showAddressStep}>
      {/* showMonitoringStep={showMonitoringStep} */}
      <Formik
        initialValues={{
          yourReference: "",
          division: singleDivisionId,
          team: singleTeamId,
          title: null,
          firstName: "",
          middleName: "",
          lastName: "",
          dateOfBirth: "",
          gender: "",
        }}
        validationSchema={addChecksIndividualValidation}
        onSubmit={handleCreateIndividual}
        enableReinitialize
      >
        {(props) => (
          <Form onSubmit={props.handleSubmit} style={{ width: "510px" }}>
            <Header />
            <Flex {...rowProps}>
              <Input
                {...inputProps}
                onChange={props.handleChange}
                value={props.values.yourReference}
                placeholder="Your Reference"
                label="Your reference"
                name="yourReference"
              />
            </Flex>
            {showDivisionField && (
              <Flex {...rowProps}>
                <Select
                  {...inputProps}
                  onChange={(option: any) => {
                    const newDivisionTeams = teamOptionsFor(option.value);
                    props.setValues({
                      ...props.values,
                      division: option.value,
                      team:
                        newDivisionTeams.length === 1
                          ? newDivisionTeams[0].value
                          : "",
                    });
                  }}
                  value={divisionOptions?.find(
                    ({ value }: any) => value === props.values.division
                  )}
                  options={divisionOptions}
                  placeholder="Choose division"
                  label="Division"
                  name="division"
                  isRequired
                  hasError={Boolean(props.errors.division)}
                  errorMessage={props.errors.division as string}
                />
              </Flex>
            )}
            {teamOptionsFor(props.values.division).length > 0 && (
              <Flex {...rowProps}>
                <Select
                  {...inputProps}
                  key={props.values.division}
                  onChange={(option: any) =>
                    props.setFieldValue("team", option.value)
                  }
                  value={
                    teamOptionsFor(props.values.division).find(
                      ({ value }: any) => value === props.values.team
                    ) ?? null
                  }
                  options={teamOptionsFor(props.values.division)}
                  placeholder="Choose team"
                  label="Team"
                  name="team"
                  isRequired
                  hasError={Boolean(props.errors.team)}
                  errorMessage={props.errors.team as string}
                />
              </Flex>
            )}
            <Flex {...rowProps}>
              <div style={{ width: "48%" }}>
                <Select
                  flex={1}
                  onChange={(option: any) =>
                    props.setFieldValue("title", option.value)
                  }
                  value={titleOptions?.find(
                    ({ value }) => value === props.values.title
                  )}
                  options={titleOptions}
                  placeholder="Select title"
                  label="Title"
                  name="title"
                />
              </div>
              <div style={{ width: "48%" }}>
                <Select
                  flex={1}
                  onChange={(option: any) =>
                    props.setFieldValue("gender", option.value)
                  }
                  value={genderOptions?.find(
                    ({ value }) => value === props.values.gender
                  )}
                  options={genderOptions}
                  placeholder="Select gender"
                  label="Gender"
                  name="gender"
                  isRequired
                  hasError={Boolean(props.errors.gender)}
                  errorMessage={props.errors.gender as string}
                />
              </div>
            </Flex>
            <Flex {...rowProps}>
              <Input
                {...inputProps}
                onChange={props.handleChange}
                value={props.values.firstName}
                placeholder="Enter first name"
                label="First name"
                name="firstName"
                hasError={Boolean(props.errors.firstName)}
                errorMessage={props.errors.firstName as string}
                isRequired
              />
              <Input
                {...inputProps}
                onChange={props.handleChange}
                value={props.values.middleName}
                placeholder="Enter middle(s) name"
                label="Middle name(s)"
                name="middleName"
                hasError={Boolean(props.errors.middleName)}
                errorMessage={props.errors.middleName as string}
              />
            </Flex>
            <Flex {...rowProps}>
              <Input
                {...inputProps}
                onChange={props.handleChange}
                value={props.values.lastName}
                placeholder="Enter last name"
                label="Last name"
                name="lastName"
                isRequired
                hasError={Boolean(props.errors.lastName)}
                errorMessage={props.errors.lastName as string}
              />
              <DatePicker
                value={props.values.dateOfBirth as any}
                onChange={(date: any) => {
                  props.setFieldValue("dateOfBirth", date);
                }}
                maxDate={DateTime.now()
                  .minus({ years: MIN_AGE_APPLICATION })
                  .toJSDate()}
                label="Date of birth"
                name="dateOfBirth"
                hasError={Boolean(props.errors.dateOfBirth)}
                errorMessage={props.errors.dateOfBirth as string}
                {...inputProps}
              />
            </Flex>

            <Flex justifyContent="flex-end">
              {/* <Button
                size="large"
                mt="2"
                mb="0"
                variant="secondary"
                onClick={() =>
                  navigate(`/applications/${mandateId}/individuals`)
                }
              >
                Back
              </Button> */}
              <Button
                size="large"
                mt="2"
                mb="0"
                type="submit"
                isDisabled={
                  (props.dirty && !props.isValid) || isLoading || doubleClick
                }
              >
                Next
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>

      <Modal
        isOpen={isNoDOBModalOpen}
        onClose={() => setIsNoDOBModalOpen(false)}
        title="No date of birth"
        content="Although date of birth is not required, it improves the accuracy of the results. We recommend providing a date of birth before proceeding."
        onModalConfirmClick={handleNext}
        secondaryButtonText="Cancel"
        primaryButtonText="Continue anyway"
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
      />
    </NewChecksFrame>
  );
};
